// Human-readable invoice rendering via the OFFICIAL xrechnung-visualization XSLT (BRIEF §4.4,
// D-028/D-035). Two-stage pipeline: source syntax → "XR" semantic-model intermediate → HTML.
// Both stages are compiled to SEF at build time (scripts/prepare-assets.mjs) and executed here
// via Saxon-JS entirely client-side.
//
// The HTML stage inlines its viewer CSS/JS via unparsed-text() and loads German/English field
// labels via doc('l10n/<lang>.xml') — both with RELATIVE paths. Saxon-JS resolves those relative
// to the transform's *document* base URI by default (i.e. the app's page URL), not to wherever
// the SEF itself was fetched from — verified empirically (D-035): without an explicit base, both
// calls fail (unparsed-text throws FODC0002; the l10n doc() lookup fails softly via
// doc-available(), producing "???key???" placeholders that then break format-number()'s picture
// string). Passing `stylesheetBaseURI` pointing at /engines/ on BOTH transform() calls fixes
// both, including doc() calls made from an imported module (functions.xsl), not just the
// top-level stylesheet. Rendering is best-effort and NEVER affects the verdict: any failure
// degrades to `null` (the UI shows no preview), it does not fail validation.

import { engineAssetExists, engineBaseUri, loadSaxon, loadSef } from './assets';
import type { DocType, Syntax } from './types';

// Stage-1 stylesheet (source → XR) depends on syntax and UBL document type.
function stage1Sef(syntax: Syntax, docType: DocType): string | null {
  if (syntax === 'CII') return 'render-cii.sef.json';
  if (docType === 'CreditNote') return 'render-ubl-creditnote.sef.json';
  if (docType === 'Invoice') return 'render-ubl-invoice.sef.json';
  return null;
}
const STAGE2_SEF = 'render-xrechnung-html.sef.json';

let _available: boolean | null = null;
async function renderingAvailable(): Promise<boolean> {
  if (_available === null) _available = await engineAssetExists(STAGE2_SEF);
  return _available;
}

/**
 * Render the invoice to HTML using the official visualization stylesheets, or return null if
 * rendering is unavailable/fails. Never throws.
 */
export async function renderInvoice(
  xml: string,
  syntax: Syntax,
  docType: DocType,
): Promise<string | null> {
  try {
    if (!(await renderingAvailable())) return null;
    const s1Name = stage1Sef(syntax, docType);
    if (!s1Name) return null;

    const saxon = await loadSaxon();
    const [s1, s2] = await Promise.all([loadSef(s1Name), loadSef(STAGE2_SEF)]);
    // Must be an absolute URL (a trailing-slash "directory") — see the file-level comment (D-035).
    const stylesheetBaseURI = engineBaseUri();

    const xr = saxon.transform(
      { stylesheetInternal: s1, stylesheetBaseURI, sourceText: xml, destination: 'serialized' },
      'sync',
    ).principalResult;
    const html = saxon.transform(
      { stylesheetInternal: s2, stylesheetBaseURI, sourceText: xr, destination: 'serialized' },
      'sync',
    ).principalResult;
    return html || null;
  } catch {
    return null;
  }
}
